import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useState } from 'react';
import { useFeatures } from '@atc/features';
import _get from 'lodash/get';
import OwnerChatCta from '../OwnerChatCta/index.js';

/**
 * Renders the chat button for Gubagoo with different styling based on UI context.
 * @param {object} props - The component props
 * @param {string} props.className - CSS class name to add to the button
 * @param {string} props.label - Custom label text for the button
 * @param {function} props.onChatClick - Callback function for clicking the chat button
 * @param {string} props.uiContext - UI context to render different styles of button ('iconButton', 'iconButtonStacked', 'button', 'link')
 * @param {boolean} props.isDealerNameRelocation - Flag to determine if dealer name relocation is enabled
 * @returns {JSX.Element} The rendered chat button component
 */
import { jsx as _jsx } from "react/jsx-runtime";
const renderButton = _ref => {
  let {
    className,
    label,
    onChatClick,
    uiContext,
    isDealerNameRelocation,
    gubagooUrl,
    state,
    setState,
    ...props
  } = _ref;
  // delete invalid dom attributes before spreading
  delete props.merchantId;
  const onClickHandler = () => {
    // We're calling a window.open here instead of using the Popup component
    // because of issues with the Popup component not opening the window in some instances
    onChatClick();
    window.open(gubagooUrl, 'popup', 'height=630,width=360,scrollbars=yes,resizable=yes', false);
  };
  return /*#__PURE__*/_jsx(OwnerChatCta, {
    "data-cmp": "ownerChat",
    className: className,
    label: label,
    onChatClick: onClickHandler,
    uiContext: uiContext,
    isDealerNameRelocation: isDealerNameRelocation,
    onError: () => setState({
      hasError: true,
      isUrlValid: state.isUrlValid
    }),
    ...props
  });
};

/**
 * OwnerChatGubagoo Component
 *
 * A React component that integrates with the Gubagoo chat system for vehicle listings.
 * This component validates the provided Gubagoo URL, handles error states, and renders
 * a popup-based chat interface. It includes dealer name relocation logic and various
 * UI context options for different button styles.
 *
 * @component
 * @param {object} props - The component props
 * @param {string} [props.className] - CSS class name to apply to the chat button
 * @param {object} props.listing - Vehicle listing object containing vehicle information
 * @param {function} [props.onChatClick] - Callback function triggered when chat button is clicked
 * @param {string} [props.gubagooUrl] - The Gubagoo chat service URL
 * @param {string} [props.uiContext] - UI context for button styling ('button', 'link', 'iconButton', 'iconButtonStacked')
 * @param {string} [props.label] - Custom label text for the button
 * @returns {JSX.Element|null} The rendered component or null if there's an error or invalid URL
 */
function OwnerChatGubagoo(_ref2) {
  let {
    className,
    listing,
    onChatClick = () => {},
    gubagooUrl = '',
    uiContext,
    ...rest
  } = _ref2;
  const [state, setState] = useState({
    hasError: false,
    isUrlValid: true
  });
  const {
    dealer_name_relocation: [isDealerNameRelocationFlagEnabled]
  } = useFeatures(['dealer_name_relocation']);
  const isPrivateSeller = _get(listing, 'owner.privateSeller', false);
  const isDealerNameRelocation = isDealerNameRelocationFlagEnabled && !isPrivateSeller;

  /**
   * Validates the Gubagoo URL by making a HEAD request to check if it's accessible.
   * Updates the component state based on the validation result and logs errors to New Relic.
   * @async
   * @function validateUrl
   */
  // Validate the URL before rendering the Popup
  const validateUrl = useCallback(async () => {
    try {
      const response = await fetch(gubagooUrl, {
        method: 'HEAD'
      });
      if (!response.ok) {
        if (window && window.newrelic) {
          window.newrelic.addPageAction('ThirdPartyError', {
            thirdPartyName: 'GUBAGOO',
            component: 'OwnerChatGubagoo',
            message: 'Invalid URL',
            url: window.location.href,
            gubagooUrl
          });
        }
        setState({
          hasError: false,
          isUrlValid: false
        });
      } else {
        setState({
          hasError: false,
          isUrlValid: true
        });
      }
    } catch (error) {
      setState({
        hasError: false,
        isUrlValid: false
      });
      if (window && window.newrelic) {
        window.newrelic.addPageAction('ThirdPartyError', {
          thirdPartyName: 'GUBAGOO',
          component: 'OwnerChatGubagoo',
          message: 'An error occurred while validating the URL',
          url: window.location.href,
          gubagooUrl,
          error
        });
      }
    }
  }, [gubagooUrl]);
  useEffect(() => {
    if (gubagooUrl) {
      validateUrl();
    } else {
      setState({
        hasError: false,
        isUrlValid: false
      });
    }
  }, [gubagooUrl, validateUrl]);
  if (state.hasError || !state.isUrlValid) {
    return null;
  }
  return renderButton({
    className,
    onChatClick,
    uiContext,
    isDealerNameRelocation,
    gubagooUrl,
    state,
    setState,
    ...rest
  });
}
export default /*#__PURE__*/React.memo(OwnerChatGubagoo);